import {
	type OnboardingStep,
	type OnboardingStepDTO,
	OnboardingStepStatus,
	type TenantOnboardingDTO,
} from '@today/types'

/**
 * The onboarding wizard's navigation rules (TO-190 REQ-009–REQ-011). Pure — the
 * page and the admin-shell gate apply them; the server enforces the state machine.
 */

const isDone = (step: OnboardingStepDTO): boolean =>
	step.status === OnboardingStepStatus.COMPLETE || step.status === OnboardingStepStatus.SKIPPED

/** REQ-009 — the first step neither complete nor skipped; step 1 when every step is done. */
export const initialStep = (steps: OnboardingStepDTO[]): OnboardingStep =>
	(steps.find((step) => !isDone(step)) ?? steps[0]).step

/** REQ-010 — selectable when every required step before it is complete. */
export const isSelectable = (steps: OnboardingStepDTO[], step: OnboardingStep): boolean => {
	const index = steps.findIndex((s) => s.step === step)
	return steps.slice(0, index).every((s) => !s.required || s.status === OnboardingStepStatus.COMPLETE)
}

/** REQ-010 — "Next" is enabled only past a complete or skipped step. */
export const canGoNext = (step: OnboardingStepDTO): boolean => isDone(step)

/** REQ-010 — "Do this later" on an optional step that is not complete. */
export const showDoLater = (step: OnboardingStepDTO): boolean =>
	!step.required && step.status !== OnboardingStepStatus.COMPLETE

/** REQ-011 — an onboarded tenant with at least one optional step not complete. */
export const shouldShowResumeBanner = (onboarding: TenantOnboardingDTO): boolean =>
	onboarding.completedAt !== null &&
	onboarding.steps.some((step) => !step.required && step.status !== OnboardingStepStatus.COMPLETE)
